import LanguagePack from "../../../../../shared/lib/html5-software-library/src/ranj/language/LanguagePack";
import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import AppModel from "../../../../../shared/src/ranj/app/core/AppModel";
import AbstractMediator from "../../../../../shared/src/ranj/app/ui/AbstractMediator";
import GameConfig from "../core/model/GameConfig";
import Scenario from "../ngt/scenario/Scenario";
import ScenarioLibrary from "../ngt/scenario/ScenarioLibrary";

export default class InfoPanelMediator extends AbstractMediator {
    /**
     * 
     * @param {createjs.MovieClip} target 
     * @param {LanguagePack} languagePack 
     * @param {*} gameAssets 
     * @param {GameConfig} gameConfig 
     * @param {ScenarioLibrary} scenarioLibrary
     */
    constructor(target, dispatcher, languagePack, layoutManager, soundManager, appModel, gameAssets, gameConfig, scenarioLibrary) {
        super(target, dispatcher, languagePack, layoutManager, soundManager, appModel);
        this._gameAssets = gameAssets;
        this._gameConfig = gameConfig;
        this._scenarioLibrary = scenarioLibrary;

        this._thisDispatcher = new EventDispatcher();
    }

    initialize() {
        super.initialize();

        this._panel = this._target.contents;
        this._toggle = this.createButton(this._panel.toggle, this._handleToggle.bind(this));

        //block clicks through panel, quick and dirty
        this._target.bg.addEventListener("click", () => { });

        const scenario = this._scenarioLibrary.getCurrentScenario();
        this._initializeCast(scenario);
        this._panel.titleTextfield.text = this.getText("cast.title");
        this._panel.episodeTitleTextfield.text = this.getText(scenario.name);

        this._openY = 0;
        this._closeY = this._panel.y;

        this._animating = false;
        this._toggle.setSelected(true);

        this._forcingReadAll = false;

        this.close(true);

    }

    destroy() {
        super.destroy();
    }

    addEventListener(type, listener, scope) {
        this._thisDispatcher.addEventListener(type, listener, scope);
    }

    removeEventListener(type, listener) {
        this._dispatcher.removeEventListener(type, listener);
    }

    /**
     * 
     * @param {Scenario} scenario 
     */
    _initializeCast(scenario) {
        const castConfig = this._gameConfig.getCast();

        // NB: disable all cast elements, set description, and get top-most Y value
        let topmostY;
        for (const characterConfig of Object.values(castConfig)) {
            const characterElement = this._panel[characterConfig.castMovieClip];
            if (!characterElement) {
                continue;
            }
            if (!topmostY || topmostY > characterElement.y) {
                topmostY = characterElement.y;
            }
            this._setCenteredText(characterElement.bioTextfield, this._languagePack.getText(characterConfig.description));
            characterElement.visible = false;
        }

        // enable cast element according to characters for scenario and position them
        let positionY = topmostY;
        for (const characterId of scenario.castIds) {
            const characterConfig = castConfig[characterId];
            if (!characterConfig) {
                console.error(`[InfoPanelMediator] Could not initialize cast character, cannot find config with id ${characterId}`);
                continue;
            }
            const characterElement = this._panel[characterConfig.castMovieClip];
            characterElement.y = positionY;
            characterElement.visible = true;
            positionY += characterElement.getBounds().height + 46;
        }
    }

    set enabled(value) {
        this._toggle.setEnabled(value);
    }

    _stopNudges() {
        this.killDelayedCall(this._nudgeTimeout);
        this._nudgeTimeout = null;
        if (!!this._nudgeTarget) {
            createjs.Tween.removeTweens(this._nudgeTarget);
            this._nudgeTarget.alpha = 1;
            this._nudgeTarget = null;
        }
    }

    _scheduleNudge(delay = 6000) {
        if (!!this._nudgeTimeout) {
            this.killDelayedCall(this._nudgeTimeout);
        }
        this._nudgeTimeout = this.callDelayed(this._nudge.bind(this), delay / AppModel.speedFactor);
    }

    _nudge() {
        this.killDelayedCall(this._nudgeTimeout);
        this._nudgeTarget = this._panel.toggle;
        this.blink(this._nudgeTarget, 3);
        this._scheduleNudge(8500);
    }

    blink(target, count = 2) {
        target.alpha = 1;
        const props = { loop: count * 2 - 1, bounce: true };
        createjs.Tween.get(target, props).to({ alpha: 0 }, 500);
    }

    _handleToggle(e) {
        this._stopNudges();
        if (this._state != State.OPENED) {
            this.open();
        } else {
            this.close();
        }
    }

    open(instant = false, useNudge = false) {
        if (this._state != State.OPENED) {
            this._animateTo(this._openY, State.OPENED, instant);
            this._thisDispatcher.dispatchEvent(InfoPanelMediator.OPEN);

            if (useNudge) {
                this._scheduleNudge();
            }
        }
    }

    close(instant = false) {
        if (this._state != State.CLOSED) {
            this._animateTo(this._closeY, State.CLOSED, instant);
            this._thisDispatcher.dispatchEvent(InfoPanelMediator.CLOSE);

            this._stopNudges();
        }
    }

    _animateTo(goalY, stateAtEnd, instant) {
        const onComplete = () => {
            this._state = stateAtEnd;
            this._toggle.setSelected(this._state != State.OPENED);
        }
        const alpha = stateAtEnd == State.OPENED ? 1 : 0;
        if (instant) {
            this._panel.y = goalY;
            this._target.bg.alpha = alpha;
            onComplete();
        } else {
            const dur = Math.abs(this._panel.y - goalY) * .5;
            createjs.Tween.get(this._panel).to({ y: goalY }, dur, createjs.Ease.quadOut).call(onComplete);
            createjs.Tween.get(this._target.bg).to({ alpha: alpha }, dur, createjs.Ease.quadOut);
        }
    }

    _setCenteredText(textField, text) {
        if(textField.y0 === undefined || textField.y0 === null) {
            textField.y0 = textField.y;
            textField.height0 = textField.getMeasuredHeight() * textField.scaleY;
        }
        textField.text = text;
        const textHeight = textField.getMeasuredHeight() * textField.scaleY;
        textField.y = textField.y0 + (textField.height0 - textHeight) / 2;
    }
}


const State = { CLOSED: 0, CLOSING: 1, OPENING: 2, OPENED: 3 };

InfoPanelMediator.CLOSE = "InfoPanelView.CLOSE";
InfoPanelMediator.OPEN = "InfoPanelView.OPEN";